import { AppIcon } from '@/components/AppIcon';
import { CheckMenu } from './CheckMenu';

export interface ColumnOption {
  key: string;
  label: string;
}

/**
 * "Columns" above a grid: which of its optional columns are showing right
 * now. A controlled list — this component only renders the checkboxes and
 * reports what changed; the caller decides which columns that maps to and
 * remembers the choice (`readStored`/`writeStored`, the same as a grid
 * remembers a column width), so the choice survives to the next visit.
 */
export function ColumnToggle({
  columns,
  hidden,
  onChange,
}: {
  columns: ColumnOption[];
  hidden: ReadonlySet<string>;
  onChange: (hidden: Set<string>) => void;
}) {
  function toggle(key: string) {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  }

  return (
    <CheckMenu
      wrapClassName="columntoggle"
      menuClassName="columntoggle__menu"
      menuLabel="Show or hide columns"
      trigger={(open, toggleOpen) => (
        <button
          type="button"
          className="btn btn--ghost btn--sm btn--withicon"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={toggleOpen}
        >
          <AppIcon kind="columns" size={15} />
          Columns
        </button>
      )}
    >
      {columns.map((c) => (
        <label key={c.key} className="columntoggle__item">
          <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => toggle(c.key)} />
          <span>{c.label}</span>
        </label>
      ))}
    </CheckMenu>
  );
}
